// SPDX-License-Identifier: BUSL-1.1

/**
 * SPA-side auth utilities — API key + Better Auth JWT (issued by build-ceo-auth).
 *
 * Supports two auth modes:
 * 1. API key (localStorage) — for CLI agents and programmatic access
 * 2. Better Auth JWT (localStorage) — for human web UI users. Issued by the
 *    standalone build-ceo-auth worker via `${AUTH_BASE}/api/auth/token` after a
 *    successful sign-in. Bootstrapped on app boot from the build-ceo-auth cookie
 *    (cross-site, so we need the Bearer token to call the build.ceo API).
 *
 */

import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// Import API_BASE from api-client which has proper runtime URL resolution
import { API_BASE, AUTH_BASE } from './api-client';
import { Sentry } from './sentry';

const TOKEN_KEY = 'ceo-api-token';
const BEARER_JWT_KEY = 'cli-ceo:bearer-jwt';
const BEARER_JWT_EXPIRES_AT_KEY = 'cli-ceo:bearer-jwt-expires-at';
// 23h — slightly under build-ceo-auth's 24h JWT TTL so we never present a token that
// has already expired by the time the request lands.
const BEARER_JWT_TTL_MS = 23 * 60 * 60 * 1000;

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Read the Better Auth JWT from localStorage if it has not expired. */
export function getBearerJwt(): string | null {
  const token = localStorage.getItem(BEARER_JWT_KEY);
  if (!token) return null;
  const expiresAtRaw = localStorage.getItem(BEARER_JWT_EXPIRES_AT_KEY);
  if (expiresAtRaw) {
    const expiresAt = Number(expiresAtRaw);
    if (Number.isFinite(expiresAt) && Date.now() >= expiresAt) {
      clearBearerJwt();
      return null;
    }
  }
  return token;
}

function storeBearerJwt(token: string): void {
  localStorage.setItem(BEARER_JWT_KEY, token);
  localStorage.setItem(BEARER_JWT_EXPIRES_AT_KEY, String(Date.now() + BEARER_JWT_TTL_MS));
}

export function clearBearerJwt(): void {
  localStorage.removeItem(BEARER_JWT_KEY);
  localStorage.removeItem(BEARER_JWT_EXPIRES_AT_KEY);
}

let inFlightBearerJwtFetch: Promise<string | null> | null = null;

type FetchBearerJwtOptions = {
  forceRefresh?: boolean;
};

export type BearerJwtBootstrapFailure = {
  code: 'token_endpoint_error' | 'missing_token' | 'network_error';
  status: number | null;
  cookiePresent: boolean | null;
  cookieLength: number | null;
  body: string | null;
};

let lastBearerJwtBootstrapFailure: BearerJwtBootstrapFailure | null = null;

function setLastBearerJwtBootstrapFailure(
  failure: BearerJwtBootstrapFailure | null,
): void {
  lastBearerJwtBootstrapFailure = failure;
}

export function getLastBearerJwtBootstrapFailure(): BearerJwtBootstrapFailure | null {
  return lastBearerJwtBootstrapFailure;
}

export function resetBearerJwtFetchForTests(): void {
  inFlightBearerJwtFetch = null;
  lastBearerJwtBootstrapFailure = null;
}

/**
 * Fetch a Better Auth JWT via the API worker proxy.
 *
 * The session cookie is set on auth.build.ceo, but SameSite=Lax blocks it
 * on cross-origin AJAX requests. We call the API worker at /api/auth/token
 * instead, which proxies to auth.build.ceo. Since the browser sees this as
 * a same-origin request (console.build.ceo → api.build.ceo), the cookie IS sent.
 *
 * Returns the token (and stores it) on success, null otherwise.
 */
export async function fetchAndStoreBearerJwt(
  options?: FetchBearerJwtOptions,
): Promise<string | null> {
  const forceRefresh = options?.forceRefresh ?? false;
  const existing = getBearerJwt();
  if (existing && !forceRefresh) return existing;

  if (inFlightBearerJwtFetch) {
    return inFlightBearerJwtFetch;
  }

  inFlightBearerJwtFetch = (async () => {
    try {
      const cached = getBearerJwt();
      if (cached && !forceRefresh) {
        setLastBearerJwtBootstrapFailure(null);
        return cached;
      }

      // Use API_BASE instead of AUTH_BASE to proxy through the API worker.
      // This avoids the cross-origin cookie issue with SameSite=Lax.
      // API_BASE already includes /api (e.g. https://api.build.ceo/api)
      // so use /auth/token — NOT /api/auth/token (double /api).
      const res = await fetch(`${API_BASE}/auth/token`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        const cookiePresentHeader = res.headers.get('x-cookie-present');
        const cookieLengthHeader = res.headers.get('x-cookie-length');
        setLastBearerJwtBootstrapFailure({
          code: 'token_endpoint_error',
          status: res.status,
          cookiePresent:
            cookiePresentHeader === null ? null : cookiePresentHeader === 'true',
          cookieLength:
            cookieLengthHeader === null ? null : Number(cookieLengthHeader),
          body: body || null,
        });
        Sentry.addBreadcrumb({
          category: 'auth-bootstrap',
          message: 'jwt-bootstrap-response-not-ok',
          level: 'warning',
          data: {
            status: res.status,
            cookiePresent:
              cookiePresentHeader === null ? null : cookiePresentHeader === 'true',
            cookieLength:
              cookieLengthHeader === null ? null : Number(cookieLengthHeader),
          },
        });
        return null;
      }
      const data: { token?: string } = await res.json();
      if (!data.token) {
        setLastBearerJwtBootstrapFailure({
          code: 'missing_token',
          status: res.status,
          cookiePresent: null,
          cookieLength: null,
          body: null,
        });
        return null;
      }
      setLastBearerJwtBootstrapFailure(null);
      storeBearerJwt(data.token);
      return data.token;
    } catch (error) {
      setLastBearerJwtBootstrapFailure({
        code: 'network_error',
        status: null,
        cookiePresent: null,
        cookieLength: null,
        body: error instanceof Error ? error.message : String(error),
      });
      return null;
    } finally {
      inFlightBearerJwtFetch = null;
    }
  })();

  return inFlightBearerJwtFetch;
}

/** Result shape for `setActiveOrganization`. */
export type SetActiveOrgResult =
  | { ok: true }
  // 404 — sibling lane 3a-auth hasn't deployed the BA organization plugin yet.
  // Caller should fall through to localStorage-only behaviour.
  | { ok: false; reason: 'unavailable' }
  | { ok: false; reason: 'error'; status?: number; message?: string };

/**
 * Tell build-ceo-auth's Better Auth which org is now active for the current session.
 *
 * This mirrors the BA client's `auth.organization.setActive({ organizationId })`
 * call, but uses the REST endpoint directly so we don't need to take a runtime
 * dependency on `better-auth/client` just for this one call.
 *
 * Returns `{ ok: false, reason: 'unavailable' }` on 404 so the caller can fall
 * through to existing localStorage-only behaviour during the lane 3a-auth
 * rollout (BA organization plugin lands in a sibling lane).
 */
export async function setActiveOrganization(organizationId: string): Promise<SetActiveOrgResult> {
  try {
    const res = await fetch(`${AUTH_BASE}/api/auth/organization/set-active`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId }),
    });
    if (res.status === 404) {
      return { ok: false, reason: 'unavailable' };
    }
    if (!res.ok) {
      const message = await res.text().catch(() => '');
      return { ok: false, reason: 'error', status: res.status, message };
    }
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'network error';
    return { ok: false, reason: 'error', message };
  }
}

/** Better Auth user shape returned by GET /api/auth/get-session */
export type BetterAuthUser = {
  id: string;
  email: string;
  name?: string;
  image?: string;
  emailVerified?: boolean;
};

export type Session = { actorId: string; role: string };

type AuthState = {
  /** API key token — null when using session auth */
  token: string | null;
  /** True if either API key or session auth is active */
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True if using session cookie auth (not API key) */
  isSessionUser: boolean;
  /** User info from Better Auth session — null for API key auth */
  user: BetterAuthUser | null;
  /** Stable bootstrap error state when Better Auth session exists but JWT bootstrap fails */
  bootstrapError: BearerJwtBootstrapFailure | null;
  /** API key login (agents/CLI) */
  login: (token: string) => void;
  /** Session login (humans via Better Auth) */
  loginWithSession: (user: BetterAuthUser) => void;
  /** Logout — handles both modes */
  logout: () => Promise<void>;
};

const noop = () => {};

export const AuthContext = createContext<AuthState>({
  token: null,
  isAuthenticated: false,
  isLoading: true,
  isSessionUser: false,
  user: null,
  bootstrapError: null,
  login: noop,
  loginWithSession: noop,
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

/** Call GET /api/auth/get-session to check if we have a valid Better Auth session. */
async function fetchBetterAuthSession(): Promise<BetterAuthUser | null> {
  try {
    const res = await fetch(`${AUTH_BASE}/api/auth/get-session`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    // Better Auth returns `null` (with HTTP 200) when there is no session.
    const data = (await res.json()) as { user?: BetterAuthUser } | null;
    return data?.user ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<BetterAuthUser | null>(null);
  const [bootstrapError, setBootstrapError] = useState<BearerJwtBootstrapFailure | null>(null);
  const initStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    async function init() {
      initStartedAtRef.current = Date.now();
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        message: 'auth-init-started',
        level: 'info',
      });

      // Check for Better Auth session (human users). build-ceo-auth lives on a
      // different origin than the UI, so the session cookie can't be read
      // cross-site — we need to mint a Bearer JWT to call the build.ceo API.
      const sessionUser = await fetchBetterAuthSession();
      if (sessionUser) {
        Sentry.addBreadcrumb({
          category: 'auth-bootstrap',
          message: 'session-user-detected',
          level: 'info',
          data: {
            elapsedMs: initStartedAtRef.current ? Date.now() - initStartedAtRef.current : null,
          },
        });
        // Bootstrap the JWT from the build-ceo-auth cookie if we don't have one yet
        // (e.g. fresh tab after OAuth redirect, or a returning user whose JWT
        // expired but cookie is still valid).
        let jwt = getBearerJwt();
        if (!jwt) {
          Sentry.addBreadcrumb({
            category: 'auth-bootstrap',
            message: 'jwt-bootstrap-started',
            level: 'info',
          });
          jwt = await fetchAndStoreBearerJwt();
          Sentry.addBreadcrumb({
            category: 'auth-bootstrap',
            message: jwt ? 'jwt-bootstrap-succeeded' : 'jwt-bootstrap-missing',
            level: jwt ? 'info' : 'warning',
            data: {
              elapsedMs: initStartedAtRef.current ? Date.now() - initStartedAtRef.current : null,
            },
          });
        }
        if (!jwt) {
          const failure = getLastBearerJwtBootstrapFailure();
          setBootstrapError(failure);
          clearBearerJwt();
          clearStoredToken();
          setUser(null);
          setToken(null);
          setIsLoading(false);
          Sentry.captureMessage(
            'jwt bootstrap failed after successful Better Auth session detection',
            {
              level: 'warning',
              extra: {
                status: failure?.status ?? null,
                code: failure?.code ?? null,
                cookiePresent: failure?.cookiePresent ?? null,
                cookieLength: failure?.cookieLength ?? null,
              },
            },
          );
          return;
        }
        setBootstrapError(null);
        setUser(sessionUser);
        setIsLoading(false);
        return;
      }
      // Fall back to API key (agents/CLI). Drop any stale BA JWT first so we
      // don't send it on subsequent apiFetch calls.
      clearBearerJwt();
      setBootstrapError(null);
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        message: 'session-user-missing-fallback-to-api-key',
        level: 'info',
        data: {
          elapsedMs: initStartedAtRef.current ? Date.now() - initStartedAtRef.current : null,
        },
      });
      setToken(getStoredToken());
      setIsLoading(false);
    }
    init();
  }, []);

  function login(newToken: string): void {
    storeToken(newToken);
    setToken(newToken);
    setUser(null);
    setBootstrapError(null);
  }

  function loginWithSession(sessionUser: BetterAuthUser): void {
    setBootstrapError(null);
    setUser(sessionUser);
    setToken(null);
  }

  async function logout(): Promise<void> {
    try {
      // Sign out via Better Auth on build-ceo-auth (no-op for API-key sessions).
      await fetch(`${AUTH_BASE}/api/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore network errors — local logout proceeds regardless
    }
    clearBearerJwt();
    clearStoredToken();
    setToken(null);
    setUser(null);
    setBootstrapError(null);
  }

  const value: AuthState = {
    token,
    isAuthenticated: token !== null || (user !== null && bootstrapError === null),
    isLoading,
    isSessionUser: user !== null,
    user,
    bootstrapError,
    login,
    loginWithSession,
    logout,
  };

  if (isLoading) return null;

  return createElement(AuthContext.Provider, { value }, children);
}
